import { useEffect, useRef } from 'react';

import { cn } from 'lib/helpers/utility';
import useLoading from 'lib/hooks/useLoading';

import Icon from 'components/shared/Icon/Icon';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const ActionWrapper = ({
  actionContentClassName,
  isOpen,
  title,
  hasExport,
  handleExportPdf,
  onClose,
  overlay,
  children
}) => {
  const contentRef = useRef(null);

  const { loading: isExporting, startLoading, stopLoading } = useLoading();

  const onExportPdf = async () => {
    try {
      startLoading();
      await handleExportPdf();
    } catch (error) {
      console.error('Error exporting an activity log to a PDF:', error);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [children]);

  return (
    <div
      className={cn(
        'z-20 flex flex-col overflow-x-clip bg-white shadow-[-2px_0px_4px_rgba(42,92,138,0.1)] transition-[width] duration-300 ease-in-out',
        actionContentClassName,
        overlay
          ? cn('absolute right-14 top-0 h-full max-w-[512px]', isOpen ? 'w-[512px]' : 'w-0')
          : cn(
              'lg:static lg:h-full sm:absolute sm:bottom-0 sm:right-14 sm:top-0',
              isOpen ? 'w-[512px] sm:w-[400px] xs:w-max' : 'w-0'
            )
      )}>
      <div className="flex h-12 w-full items-center justify-between border-b border-solid border-white border-b-neutral-200 bg-white px-5 py-3">
        <Typography color="neutral-800" size="small" weight="semibold" className="leading-[16px]">
          {title}
        </Typography>

        <div className="flex items-center gap-1">
          {hasExport && (
            <Popover
              isFixed
              position="right"
              icon="context-menu-vertical"
              iconSize={18}
              iconColor="neutral"
              isBorder={false}
              buttonClassName="!min-h-0 !min-w-0 size-[26px] !rounded-[3px]"
              panelClassName="!fixed z-[99999]"
              options={[
                {
                  label: isExporting ? 'Exporting...' : 'Export logs',
                  icon: 'export-mono',
                  onClick: onExportPdf,
                  disabled: isExporting
                }
              ]}
            />
          )}

          <Icon
            icon="close-linear"
            color="neutral-700"
            size={18}
            className="cursor-pointer"
            onClick={onClose}
          />
        </div>
      </div>
      <div ref={contentRef} className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

export default ActionWrapper;
